import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Create Client' },
    { label: 'Verify Credentials' },
    { label: 'Submit OTP' },
    { label: 'Fetch Data' },
];

const ClientIdBadge = ({ clientId }) => (
    clientId ? (
        <div className="mb-3 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
            <i className="pi pi-key text-primary text-sm" />
            <span className="text-600 text-xs">Client ID:</span>
            <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
        </div>
    ) : null
);

/**
 * One Surepass action tile: a simple one-click action, or a wrapper around a
 * caller-supplied form when the action needs input.
 *
 * MODULE SCOPE IS LOAD-BEARING — do not move this inside ItrFlowComponent (#402).
 *
 * React identifies a component by its function reference. Declared in a render
 * body, this gets a fresh reference on every parent render, so React sees a
 * different component type, unmounts the old tree and mounts a new one. The
 * Advanced-Query cards below hold controlled inputs, and every keystroke calls
 * setDataInputs → parent re-renders → the InputText DOM node is destroyed and
 * recreated → focus is lost. The DSA had to click back into the field for each
 * character, which made the billable Get ITR / 26AS / AIS / Reset Password
 * actions usable only by pasting.
 *
 * useCallback and useMemo do NOT fix this. They stabilise a value, but the
 * remount is triggered by the component TYPE changing identity; only a
 * reference that outlives the render — module scope — is stable enough. That is
 * why everything this needs arrives as a prop rather than through a closure.
 *
 * `result` is rendered only on the no-children path. A card that supplies its
 * own children also supplies its own result block, so rendering it here too
 * printed the whole Surepass output twice.
 */
const ActionCard = ({ label, icon, description, loading, result, onRun, children }) => (
    <div className="col-12 md:col-6 lg:col-4">
        <Card className="h-full">
            <div className="flex align-items-center gap-2 mb-2">
                <i className={`pi ${icon} text-primary`} />
                <span className="font-semibold text-900 text-sm">{label}</span>
            </div>
            <p className="text-500 text-xs mb-3 m-0">{description}</p>
            {children || (
                <>
                    <Button label={label} icon="pi pi-play" size="small" loading={loading} disabled={loading}
                        onClick={onRun} className="w-full" outlined />
                    {result && (
                        <div className="mt-2">
                            <GenericSurepassOutputComponent reportData={result} title={label} icon={icon} hideCredits />
                        </div>
                    )}
                </>
            )}
        </Card>
    </div>
);

const ItrFlowComponent = () => {
    const { createId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // All ITR service IDs discovered from categorized API
    const [svcIds, setSvcIds] = useState({});

    // Shared state — client_id flows through all steps
    const [clientId, setClientId] = useState('');

    // Step 0 — Create Client
    const [pan, setPan] = useState('');
    const [password, setPassword] = useState('');
    const [createConsent, setCreateConsent] = useState(false);
    const [createErrors, setCreateErrors] = useState({});
    const [createLoading, setCreateLoading] = useState(false);
    const [createResult, setCreateResult] = useState(null);

    // Step 1 — Check Credentials
    const [checkLoading, setCheckLoading] = useState(false);
    const [checkResult, setCheckResult] = useState(null);
    const [checkConsent, setCheckConsent] = useState(false);

    // Step 2 — Submit OTP
    const [otp, setOtp] = useState('');
    const [otpConsent, setOtpConsent] = useState(false);
    const [otpErrors, setOtpErrors] = useState({});
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpResult, setOtpResult] = useState(null);

    // Step 3 — Data actions (each action has independent loading + result)
    const [dataLoading, setDataLoading] = useState({});
    const [dataResults, setDataResults] = useState({});
    const [dataInputs, setDataInputs] = useState({ itrId: '', tdsId: '', financialYear: '', newPassword: '' });

    // ── Bootstrap ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!createId) { setFetchingService(false); return; }
        Promise.all([
            axiosInstance.get(`/user/report-services/${createId}`),
            axiosInstance.get('/user/report-services/categorized'),
        ])
            .then(([svcRes, catRes]) => {
                if (svcRes.data.success) setServiceDetails(svcRes.data.data);
                const allServices = (catRes.data.data || []).flatMap(c => c.services || []);
                const itr = {};
                allServices.forEach(s => {
                    const u = (s.url || '').toLowerCase();
                    if (!u.includes('/itr/')) return;
                    if (u.includes('create-client'))      itr.create         = s.id;
                    else if (u.includes('check-cred'))    itr.check          = s.id;
                    else if (u.includes('submit-otp'))    itr.otp            = s.id;
                    else if (u.includes('download-profile')) itr.dlProfile   = s.id;
                    else if (u.includes('get-profile'))   itr.getProfile     = s.id;
                    else if (u.includes('download-itr-v')) itr.dlItrV        = s.id;
                    else if (u.includes('download-itr'))  itr.dlItr          = s.id;
                    else if (u.includes('download-26as')) itr.dl26as         = s.id;
                    else if (u.includes('get-itr-details')) itr.getItr       = s.id;
                    else if (u.includes('get-26as-details')) itr.get26as     = s.id;
                    else if (u.includes('download-ais-v2')) itr.dlAisV2      = s.id;
                    else if (u.includes('get-ais'))       itr.getAis         = s.id;
                    else if (u.includes('forget-password')) itr.forgot       = s.id;
                });
                // fallback: createId covers anything not found
                setSvcIds(itr);
            })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [createId]);

    const sid = (key) => svcIds[key] || parseInt(createId, 10);

    // ── Error helpers ────────────────────────────────────────────────────────
    const showApiError = (error, summary) => {
        const s = error.response?.status;
        const d = error.response?.data;
        let detail;
        if (s === 422) {
            const first = d?.errors ? Object.values(d.errors)[0]?.[0] : null;
            detail = first || d?.message || 'Validation failed';
        } else if (s === 402) {
            const c = d || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        } else {
            detail = d?.message || error.message || summary;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    const extractClientId = (resData) => {
        const d = resData?.data?.data || resData?.data || {};
        return d.client_id || d.clientId || '';
    };

    // ── Step 0: Create Client ────────────────────────────────────────────────
    const validateCreate = () => {
        const e = {};
        if (!pan.trim()) e.pan = 'PAN is required';
        else if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(pan.trim())) e.pan = 'Invalid PAN format (e.g. ABCDE1234F)';
        if (!password) e.password = 'Password is required';
        if (!createConsent) e.consent = 'Please confirm consent before submitting';
        return e;
    };

    const doCreateClient = async () => {
        setCreateLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/itr/create-client', {
                username: pan.trim(),
                password,
                api_provider_service_id: sid('create'),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Create client failed');
            setCreateResult(res.data);
            const cid = extractClientId(res.data);
            setClientId(cid);
            toast.current?.show({ severity: 'success', summary: 'ITR client created', detail: 'Proceed to verify credentials', life: 2500 });
        } catch (err) {
            showApiError(err, 'Create client failed');
        } finally {
            setCreateLoading(false);
            submittingRef.current = false;
        }
    };

    const handleCreateSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateCreate();
        if (Object.keys(errs).length) { setCreateErrors(errs); return; }
        setCreateErrors({});
        submittingRef.current = true;
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">PAN:</span> <span className="font-medium">{pan}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="font-medium">{serviceDetails?.available_credits ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="font-medium">{serviceDetails?.credits_required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm ITR client creation',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, create',
            rejectLabel: 'Cancel',
            accept: doCreateClient,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // ── Step 1: Check Credentials ────────────────────────────────────────────
    const doCheckCredentials = async () => {
        if (!clientId) return;
        setCheckLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/itr/check-credentials', {
                client_id: clientId,
                api_provider_service_id: sid('check'),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Credential check failed');
            setCheckResult(res.data);
            toast.current?.show({ severity: 'info', summary: 'OTP triggered', detail: 'An OTP has been sent to your registered mobile', life: 3500 });
        } catch (err) {
            showApiError(err, 'Credential check failed');
        } finally {
            setCheckLoading(false);
        }
    };

    // ── Step 2: Submit OTP ───────────────────────────────────────────────────
    const doSubmitOtp = async () => {
        setOtpLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/itr/submit-otp', {
                client_id: clientId,
                otp: otp.trim(),
                api_provider_service_id: sid('otp'),
            });
            if (!res.data.success) throw new Error(res.data.message || 'OTP verification failed');
            setOtpResult(res.data);
            toast.current?.show({ severity: 'success', summary: 'Authenticated', detail: 'ITR portal access granted', life: 2500 });
        } catch (err) {
            showApiError(err, 'OTP verification failed');
        } finally {
            setOtpLoading(false);
            submittingRef.current = false;
        }
    };

    const handleOtpSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = {};
        if (!otp.trim()) errs.otp = 'OTP is required';
        else if (!/^\d{4,8}$/.test(otp.trim())) errs.otp = 'Enter a valid OTP';
        if (!otpConsent) errs.consent = 'Please confirm consent';
        if (Object.keys(errs).length) { setOtpErrors(errs); return; }
        setOtpErrors({});
        submittingRef.current = true;
        doSubmitOtp();
    };

    // ── Step 3: Data actions ─────────────────────────────────────────────────
    const setActionLoading = (key, val) => setDataLoading(p => ({ ...p, [key]: val }));
    const setActionResult  = (key, val) => setDataResults(p => ({ ...p, [key]: val }));

    const runAction = async (key, fn) => {
        setActionLoading(key, true);
        try {
            const res = await fn();
            if (!res.data.success) throw new Error(res.data.message || 'Request failed');
            setActionResult(key, res.data);
            toast.current?.show({ severity: 'success', summary: 'Done', detail: 'Result is ready below', life: 2000 });
        } catch (err) {
            showApiError(err, 'Request failed');
        } finally {
            setActionLoading(key, false);
        }
    };

    const dataActions = [
        {
            key: 'getProfile',
            label: 'Get Profile',
            icon: 'pi-user',
            description: 'Fetch ITR profile details',
            call: () => axiosInstance.get('/surepass/itr/get-profile', { params: { client_id: clientId, api_provider_service_id: sid('getProfile') } }),
        },
        {
            key: 'dlProfile',
            label: 'Download Profile',
            icon: 'pi-download',
            description: 'Initiate profile data download',
            call: () => axiosInstance.post('/surepass/itr/download-profile', { client_id: clientId, api_provider_service_id: sid('dlProfile') }),
        },
        {
            key: 'dlItr',
            label: 'Download ITR',
            icon: 'pi-file',
            description: 'Initiate Income Tax Return download',
            call: () => axiosInstance.post('/surepass/itr/download-itr', { client_id: clientId, api_provider_service_id: sid('dlItr') }),
        },
        {
            key: 'dlItrV',
            label: 'Download ITR-V',
            icon: 'pi-file-pdf',
            description: 'Download ITR-V acknowledgement',
            call: () => axiosInstance.post('/surepass/itr/download-itr-v', { client_id: clientId, api_provider_service_id: sid('dlItrV') }),
        },
        {
            key: 'dl26as',
            label: 'Download 26AS',
            icon: 'pi-file-pdf',
            description: 'Initiate Form 26AS download',
            call: () => axiosInstance.post('/surepass/itr/download-26as', { client_id: clientId, api_provider_service_id: sid('dl26as') }),
        },
    ];

    // ── Reset ────────────────────────────────────────────────────────────────
    const resetFlow = () => {
        setStep(0);
        setClientId('');
        setPan(''); setPassword(''); setCreateConsent(false); setCreateErrors({}); setCreateResult(null);
        setCheckResult(null); setCheckConsent(false);
        setOtp(''); setOtpConsent(false); setOtpErrors({}); setOtpResult(null);
        setDataLoading({}); setDataResults({});
        setDataInputs({ itrId: '', tdsId: '', financialYear: '', newPassword: '' });
        submittingRef.current = false;
    };

    // ── Renderers ────────────────────────────────────────────────────────────
    const errMsg = (msg) => msg ? (
        <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{msg}</small>
    ) : null;

    const renderStep0 = () => (
        <Card>
            {!createResult ? (
                <form onSubmit={handleCreateSubmit} noValidate>
                    <div className="grid">
                        <div className="field col-12 md:col-5">
                            <label className="block font-medium text-sm mb-2">PAN / Username <span className="text-red-500">*</span></label>
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-id-card" />
                                <InputText value={pan} onChange={e => { setPan((e.target.value || '').toUpperCase()); if (createErrors.pan) setCreateErrors(p => ({ ...p, pan: '' })); }}
                                    placeholder="ABCDE1234F" maxLength={10} spellCheck={false} disabled={createLoading}
                                    className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!createErrors.pan })} />
                            </IconField>
                            {errMsg(createErrors.pan)}
                        </div>
                        <div className="field col-12 md:col-5">
                            <label className="block font-medium text-sm mb-2">ITR Portal Password <span className="text-red-500">*</span></label>
                            <Password value={password} onChange={e => { setPassword(e.target.value); if (createErrors.password) setCreateErrors(p => ({ ...p, password: '' })); }}
                                placeholder="Enter password" maxLength={64} feedback={false} toggleMask disabled={createLoading}
                                inputClassName={classNames('w-full p-inputtext-sm', { 'p-invalid': !!createErrors.password })}
                                className="w-full" />
                            {errMsg(createErrors.password)}
                        </div>
                    </div>
                    <ConsentCheckbox checked={createConsent}
                        onChange={e => { setCreateConsent(e.checked); if (createErrors.consent) setCreateErrors(p => ({ ...p, consent: '' })); }}
                        error={createErrors.consent} reportType="itr-create-client" />
                    <div className="flex justify-content-end mt-3">
                        <Button type="submit" label="Create Client" icon="pi pi-arrow-right" iconPos="right" loading={createLoading} disabled={createLoading} size="small" />
                    </div>
                </form>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={createResult} title="ITR Client Created" icon="pi-user-plus" />
                    <ClientIdBadge clientId={clientId} />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Start Over" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={resetFlow} />
                        <Button label="Check Credentials" icon="pi pi-arrow-right" iconPos="right" size="small" onClick={() => setStep(1)} />
                    </div>
                </>
            )}
        </Card>
    );

    const renderStep1 = () => (
        <Card>
            <ClientIdBadge clientId={clientId} />
            {!checkResult ? (
                <>
                    <p className="text-600 text-sm mb-3">
                        Click below to validate your ITR portal credentials. An OTP will be sent to your registered mobile number.
                    </p>
                    <ConsentCheckbox checked={checkConsent} onChange={e => setCheckConsent(e.checked)} reportType="itr-check-credentials" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} disabled={checkLoading} />
                        <Button label="Check Credentials" icon="pi pi-send" iconPos="right" loading={checkLoading}
                            disabled={checkLoading || !checkConsent} size="small" onClick={doCheckCredentials} />
                    </div>
                </>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={checkResult} title="Credentials Verified" icon="pi-key" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} />
                        <Button label="Enter OTP" icon="pi pi-arrow-right" iconPos="right" size="small" onClick={() => setStep(2)} />
                    </div>
                </>
            )}
        </Card>
    );

    const renderStep2 = () => (
        <Card>
            <ClientIdBadge clientId={clientId} />
            {!otpResult ? (
                <form onSubmit={handleOtpSubmit} noValidate>
                    <div className="grid">
                        <div className="field col-12 md:col-4">
                            <label className="block font-medium text-sm mb-2">OTP <span className="text-red-500">*</span></label>
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-lock" />
                                <InputText value={otp} onChange={e => { setOtp((e.target.value || '').replace(/\D/g, '')); if (otpErrors.otp) setOtpErrors(p => ({ ...p, otp: '' })); }}
                                    placeholder="6-digit OTP" maxLength={8} inputMode="numeric" autoComplete="one-time-code" disabled={otpLoading}
                                    className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!otpErrors.otp })} />
                            </IconField>
                            {errMsg(otpErrors.otp)}
                            <small className="text-500 block mt-1">Check your ITR-registered mobile for the OTP</small>
                        </div>
                    </div>
                    <ConsentCheckbox checked={otpConsent}
                        onChange={e => { setOtpConsent(e.checked); if (otpErrors.consent) setOtpErrors(p => ({ ...p, consent: '' })); }}
                        error={otpErrors.consent} reportType="itr-submit-otp" />
                    <div className="flex justify-content-between mt-3">
                        <Button type="button" label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(1)} disabled={otpLoading} />
                        <Button type="submit" label="Verify OTP" icon="pi pi-check" iconPos="right" loading={otpLoading} disabled={otpLoading} size="small" />
                    </div>
                </form>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={otpResult} title="OTP Verified — Authenticated" icon="pi-check-circle" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(1)} />
                        <Button label="Fetch ITR Data" icon="pi pi-arrow-right" iconPos="right" size="small" onClick={() => setStep(3)} />
                    </div>
                </>
            )}
        </Card>
    );

    const renderStep3 = () => (
        <div>
            <ClientIdBadge clientId={clientId} />

            {/* Simple one-click actions */}
            <h3 className="text-base font-semibold text-900 mb-2 mt-1">Profile &amp; Returns</h3>
            <div className="grid mb-3">
                {dataActions.map(a => (
                    <ActionCard key={a.key} label={a.label} icon={a.icon} description={a.description}
                        loading={dataLoading[a.key]} result={dataResults[a.key]}
                        onRun={() => runAction(a.key, a.call)} />
                ))}
            </div>

            <Divider />

            {/* Actions needing extra input */}
            <h3 className="text-base font-semibold text-900 mb-2">Advanced Queries</h3>
            <div className="grid mb-3">
                {/* Get ITR Details */}
                <ActionCard label="Get ITR Details" icon="pi-file" description="Fetch ITR details using an ITR ID">
                    <div className="field mb-2">
                        <InputText value={dataInputs.itrId} onChange={e => setDataInputs(p => ({ ...p, itrId: e.target.value }))}
                            placeholder="ITR ID" maxLength={100} className="w-full p-inputtext-sm font-mono" />
                    </div>
                    <Button label="Get ITR Details" icon="pi pi-play" size="small" loading={dataLoading.getItr} disabled={dataLoading.getItr || !dataInputs.itrId.trim()}
                        onClick={() => runAction('getItr', () => axiosInstance.get('/surepass/itr/get-itr-details', { params: { client_id: clientId, itr_id: dataInputs.itrId.trim(), api_provider_service_id: sid('getItr') } }))}
                        className="w-full" outlined />
                    {dataResults.getItr && <div className="mt-2"><GenericSurepassOutputComponent reportData={dataResults.getItr} title="ITR Details" icon="pi-file" hideCredits /></div>}
                </ActionCard>

                {/* Get 26AS Details */}
                <ActionCard label="Get 26AS Details" icon="pi-file-pdf" description="Fetch 26AS details using a TDS ID">
                    <div className="field mb-2">
                        <InputText value={dataInputs.tdsId} onChange={e => setDataInputs(p => ({ ...p, tdsId: e.target.value }))}
                            placeholder="TDS ID" maxLength={100} className="w-full p-inputtext-sm font-mono" />
                    </div>
                    <Button label="Get 26AS Details" icon="pi pi-play" size="small" loading={dataLoading.get26as} disabled={dataLoading.get26as || !dataInputs.tdsId.trim()}
                        onClick={() => runAction('get26as', () => axiosInstance.get('/surepass/itr/get-26as-details', { params: { client_id: clientId, tds_id: dataInputs.tdsId.trim(), api_provider_service_id: sid('get26as') } }))}
                        className="w-full" outlined />
                    {dataResults.get26as && <div className="mt-2"><GenericSurepassOutputComponent reportData={dataResults.get26as} title="26AS Details" icon="pi-file-pdf" hideCredits /></div>}
                </ActionCard>

                {/* Get AIS */}
                <ActionCard label="Get AIS" icon="pi-chart-bar" description="Fetch Annual Information Statement">
                    <div className="field mb-2">
                        <InputText value={dataInputs.financialYear} onChange={e => setDataInputs(p => ({ ...p, financialYear: e.target.value }))}
                            placeholder="Financial Year (e.g. 2024-25)" maxLength={10} className="w-full p-inputtext-sm font-mono" />
                    </div>
                    <Button label="Get AIS" icon="pi pi-play" size="small" loading={dataLoading.getAis} disabled={dataLoading.getAis || !dataInputs.financialYear.trim()}
                        onClick={() => runAction('getAis', () => axiosInstance.post('/surepass/itr/get-ais', { client_id: clientId, financial_year: dataInputs.financialYear.trim(), api_provider_service_id: sid('getAis') }))}
                        className="w-full" outlined />
                    {dataResults.getAis && <div className="mt-2"><GenericSurepassOutputComponent reportData={dataResults.getAis} title="AIS" icon="pi-chart-bar" hideCredits /></div>}
                </ActionCard>

                {/* Download AIS v2 */}
                <ActionCard label="Download AIS v2" icon="pi-download" description="Download AIS Annual Information Statement v2">
                    <div className="field mb-2">
                        <InputText value={dataInputs.financialYear} onChange={e => setDataInputs(p => ({ ...p, financialYear: e.target.value }))}
                            placeholder="Financial Year (e.g. 2024-25)" maxLength={10} className="w-full p-inputtext-sm font-mono" />
                    </div>
                    <Button label="Download AIS v2" icon="pi pi-play" size="small" loading={dataLoading.dlAisV2} disabled={dataLoading.dlAisV2 || !dataInputs.financialYear.trim()}
                        onClick={() => runAction('dlAisV2', () => axiosInstance.post('/surepass/itr/download-ais-v2', { client_id: clientId, financial_year: dataInputs.financialYear.trim(), api_provider_service_id: sid('dlAisV2') }))}
                        className="w-full" outlined />
                    {dataResults.dlAisV2 && <div className="mt-2"><GenericSurepassOutputComponent reportData={dataResults.dlAisV2} title="AIS v2" icon="pi-download" hideCredits /></div>}
                </ActionCard>

                {/* Forgot Password */}
                <ActionCard label="Reset Password" icon="pi-lock-open" description="Reset ITR portal password for this client">
                    <div className="field mb-2">
                        <Password value={dataInputs.newPassword} onChange={e => setDataInputs(p => ({ ...p, newPassword: e.target.value }))}
                            placeholder="New password" maxLength={64} feedback={false} toggleMask
                            inputClassName="w-full p-inputtext-sm" className="w-full" />
                    </div>
                    <Button label="Reset Password" icon="pi pi-play" size="small" loading={dataLoading.forgot} disabled={dataLoading.forgot || !dataInputs.newPassword}
                        onClick={() => runAction('forgot', () => axiosInstance.post('/surepass/itr/forget-password', { client_id: clientId, password: dataInputs.newPassword, api_provider_service_id: sid('forgot') }))}
                        className="w-full" outlined />
                    {dataResults.forgot && <div className="mt-2"><GenericSurepassOutputComponent reportData={dataResults.forgot} title="Password Reset" icon="pi-lock-open" hideCredits /></div>}
                </ActionCard>
            </div>

            <Divider />
            <div className="flex justify-content-between">
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(2)} />
                <Button label="Start New Session" icon="pi pi-refresh" outlined size="small" onClick={resetFlow} />
            </div>
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-file-edit"
                        title="ITR Verification Flow"
                        subtitle="Create an ITR client, verify credentials via OTP, then fetch profile, returns, 26AS, AIS and more"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default ItrFlowComponent;
